import React, { useState, useEffect, useContext } from "react";
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Fragment } from 'react';
import { getCompanySettings, updateCompanySettings } from '../services/api_settings';
import { getJustificationsCompanyList, updateJustificationTeamCalendarStatus } from '../services/api_justifications';
import { AuthContext } from '../contexts/auth';
import Swal from "sweetalert2";
import Fetching from "./fetching";


const SCOPE_OPTIONS = [
  {
    value: 'ou',
    label: 'Colleghi della stessa unità organizzativa',
    description: 'Ogni dipendente vede solo i colleghi che appartengono alle sue stesse unità organizzative.',
  },
  {
    value: 'company',
    label: "Tutta l'azienda",
    description: "Ogni dipendente vede tutti i colleghi con un contratto attivo in azienda.",
  },
];

const STATUS_OPTIONS = [
  { value: 'absent', label: 'Assente' },
  { value: 'smart', label: 'Smart working' },
  { value: 'away', label: 'Trasferta' },
  { value: 'present', label: 'Presente (ignorato)' },
];

const TeamCalendarSetting = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [scope, setScope] = useState('ou');
  const [justifications, setJustifications] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [settings, companyJustifications] = await Promise.all([
          getCompanySettings(token, 'attendance'),
          getJustificationsCompanyList(token, 'attendance', true),
        ]);
        setJustifications(companyJustifications || []);
        setEnabled(settings?.team_calendar_enabled || false);
        setScope(settings?.team_calendar_scope || 'ou');
      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: "error",
          title: "Errore",
          text: "Errore durante il caricamento delle impostazioni.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [token]);

  const save = async (request, rollback) => {
    setIsSaving(true);
    try {
      await request();
      Swal.fire({
        icon: "success",
        title: "Salvato!",
        showConfirmButton: false,
        timer: 1000,
      });
    } catch (err) {
      console.error(err);
      rollback();
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: "Si è verificato un errore durante il salvataggio. Riprova più tardi.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = () => {
    const previous = enabled;
    setEnabled(!previous);
    save(() => updateCompanySettings(token, { team_calendar_enabled: !previous }), () => setEnabled(previous));
  };

  const handleScopeChange = (value) => {
    if (value === scope) return;
    const previous = scope;
    setScope(value);
    save(() => updateCompanySettings(token, { team_calendar_scope: value }), () => setScope(previous));
  };

  const setJustificationStatus = (id, value) => {
    setJustifications((prev) => prev.map((j) => (j.id === id ? { ...j, team_calendar_status: value } : j)));
  };

  const handleStatusChange = (justification, value) => {
    const previous = justification.team_calendar_status;
    setJustificationStatus(justification.id, value);
    save(
      () => updateJustificationTeamCalendarStatus(token, justification.id, value),
      () => setJustificationStatus(justification.id, previous),
    );
  };

  if (loading) {
    return <Fetching />;
  }

  return (
    <div className="max-w-full p-4 mx-auto">
      <div className="border border-gray-300 rounded-lg p-3">
        <div className="relative flex items-center py-2 mb-3">
          <div className="min-w-0 flex-1 text-sm font-semibold text-gray-900">
            <div>Attiva il calendario "Il mio team" per i dipendenti</div>
          </div>
          <div className="ml-3 flex h-6 items-center">
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label='Attiva il calendario "Il mio team"'
              onClick={handleToggle}
              disabled={isSaving}
              className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none disabled:opacity-60 ${
                enabled ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className='ml-2 font-semibold text-gray-800'>Stato: {enabled ? 'Abilitato' : 'Disabilitato'}</span>
          </div>
        </div>

        <span className="text-sm text-gray-900">
          I dipendenti vedono nell'app la disponibilità dei colleghi giorno per giorno: presente, smart working,
          trasferta, assente o non lavorativo. Il motivo delle assenze (ferie, malattia, permessi…) non viene mai mostrato.
        </span>

        {enabled && (
          <fieldset className="mt-6" disabled={isSaving}>
            <legend className="text-sm font-semibold text-gray-900 mb-3">Chi può vedere ciascun dipendente</legend>
            <div className="space-y-3">
              {SCOPE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="team_calendar_scope"
                    value={option.value}
                    checked={scope === option.value}
                    onChange={() => handleScopeChange(option.value)}
                    className="mt-1 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                    <span className="block text-sm text-gray-500">{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {enabled && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900">Come appare ogni giustificativo ai colleghi</h3>
            <p className="text-sm text-gray-500 mb-3">
              "Assente" non distingue il motivo. "Presente" fa ignorare il giustificativo (es. formazione in sede, straordinario).
              Le richieste non ancora approvate non vengono mostrate.
            </p>
            <div className="w-full overflow-x-auto max-h-[50vh] overflow-y-auto">
              <table className="table-auto w-full text-sm border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left border border-gray-300 font-semibold text-gray-600">Giustificativo</th>
                    <th className="px-4 py-2 text-left border border-gray-300 font-semibold text-gray-600">Stato nel calendario team</th>
                  </tr>
                </thead>
                <tbody>
                  {justifications.map((justification) => {
                    const selectedStatus =
                      STATUS_OPTIONS.find(
                        (option) =>
                          option.value === (justification.team_calendar_status || 'absent')
                      ) || STATUS_OPTIONS[0];

                    return (
                      <tr
                        key={justification.id}
                        className="border border-gray-300"
                      >
                        <td className="border-r border-gray-300 px-4 py-2 uppercase text-gray-700">
                          {justification.justification?.code} - {justification.justification?.description}
                        </td>

                        <td className="px-4 py-2">
                          <Listbox
                            value={selectedStatus.value}
                            onChange={(value) => handleStatusChange(justification, value)}
                            disabled={isSaving}
                          >
                            <div className="relative w-full">
                              <Listbox.Button
                                aria-label={`Stato nel calendario team per ${justification.justification?.description}`}
                                className="relative flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <span className="block truncate">
                                  {selectedStatus.label}
                                </span>

                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                  <ChevronUpDownIcon
                                    className="h-5 w-5 text-indigo-500"
                                    aria-hidden="true"
                                  />
                                </span>
                              </Listbox.Button>

                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <Listbox.Options className="absolute right-0 z-50 mt-1 max-h-60 w-full min-w-[160px] overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                                  {STATUS_OPTIONS.map((option) => (
                                    <Listbox.Option
                                      key={option.value}
                                      value={option.value}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-3 pr-9 transition-colors ${
                                          active
                                            ? 'bg-indigo-50 text-indigo-700'
                                            : 'text-gray-700'
                                        }`
                                      }
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span
                                            className={`block truncate ${
                                              selected ? 'font-semibold' : 'font-normal'
                                            }`}
                                          >
                                            {option.label}
                                          </span>

                                          {selected && (
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
                                              <CheckIcon
                                                className="h-4 w-4"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  ))}
                                </Listbox.Options>
                              </Transition>
                            </div>
                          </Listbox>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamCalendarSetting;
